import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { allocationAPI, accountsAPI, tellerAccountAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Check,
  X,
  Clock,
  CheckCircle2,
  XCircle,
  Filter,
  Banknote,
} from 'lucide-react';

const formatCurrency = (value, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${new Intl.NumberFormat('en-US').format(value || 0)}`;
  }
  return `$${new Intl.NumberFormat('en-US').format(value || 0)}`;
};

// Chart-of-Accounts asset detail types that can serve as a cash allocation
// source (physical/liquid cash only -- no separate vault entity).
const SOURCE_DETAIL_TYPES = ['bank', 'cash_and_equivalents'];

const USD_DENOMINATIONS = [
  { key: 'usd_100', label: '$100', value: 100 },
  { key: 'usd_50', label: '$50', value: 50 },
  { key: 'usd_20', label: '$20', value: 20 },
  { key: 'usd_10', label: '$10', value: 10 },
  { key: 'usd_5', label: '$5', value: 5 },
  { key: 'usd_1', label: '$1', value: 1 },
];

const LRD_DENOMINATIONS = [
  { key: 'lrd_5000', label: 'L$5,000', value: 5000 },
  { key: 'lrd_1000', label: 'L$1,000', value: 1000 },
  { key: 'lrd_500', label: 'L$500', value: 500 },
  { key: 'lrd_100', label: 'L$100', value: 100 },
  { key: 'lrd_50', label: 'L$50', value: 50 },
  { key: 'lrd_20', label: 'L$20', value: 20 },
];

function DenominationInput({ currency, denominations, onChange }) {
  const denoms = currency === 'USD' ? USD_DENOMINATIONS : LRD_DENOMINATIONS;

  const calculateTotal = () => {
    return denoms.reduce((sum, d) => sum + (denominations[d.key] || 0) * d.value, 0);
  };

  const handleChange = (key, value) => {
    const numValue = parseInt(value) || 0;
    onChange({ ...denominations, [key]: numValue });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium flex items-center gap-2">
          <Banknote className="h-4 w-4" />
          Denomination Breakdown
        </span>
        <span className="text-sm font-semibold text-primary">
          Total: {formatCurrency(calculateTotal(), currency)}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {denoms.map((d) => (
          <div key={d.key} className="relative">
            <label className="block text-xs text-muted-foreground mb-1">
              {d.label}
            </label>
            <input
              type="number"
              min="0"
              value={denominations[d.key] || ''}
              onChange={(e) => handleChange(d.key, e.target.value)}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-md"
              placeholder="0"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateAllocationModal({ isOpen, onClose, onSuccess, companyId }) {
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [tellerId, setTellerId] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [useDenominations, setUseDenominations] = useState(true);
  const [denominations, setDenominations] = useState({});
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const { data: allAccounts = [] } = useQuery({
    queryKey: ['accounts', companyId],
    queryFn: () => accountsAPI.getAll(companyId).then((res) => res.data),
    enabled: !!companyId && isOpen,
  });

  // Cash source accounts -- Chart-of-Accounts asset accounts (bank / cash
  // and equivalents) directly. There's no separate vault entity: this
  // account's own balance IS the allocation source.
  const sourceAccounts = allAccounts.filter(
    (a) => a.type === 'asset' && SOURCE_DETAIL_TYPES.includes(a.detail_type)
  );

  const { data: tellers = [] } = useQuery({
    queryKey: ['teller-accounts', companyId],
    queryFn: () => tellerAccountAPI.getAll(companyId).then((res) => res.data),
    enabled: !!companyId && isOpen,
  });

  const selectedSource = sourceAccounts.find((a) => a.id === sourceAccountId);

  // Auto-set currency based on selected source account
  const handleSourceChange = (newSourceId) => {
    setSourceAccountId(newSourceId);
    const source = sourceAccounts.find((a) => a.id === newSourceId);
    if (source) {
      setCurrency(source.currency || 'USD');
      setDenominations({});
    }
  };

  const calculateDenominationTotal = () => {
    const denoms = currency === 'USD' ? USD_DENOMINATIONS : LRD_DENOMINATIONS;
    return denoms.reduce((sum, d) => sum + (denominations[d.key] || 0) * d.value, 0);
  };

  const mutation = useMutation({
    mutationFn: (data) => allocationAPI.create(data),
    onSuccess: () => {
      toast.success('Allocation created successfully');
      onSuccess();
      onClose();
      setSourceAccountId('');
      setTellerId('');
      setAmount('');
      setDenominations({});
      setNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create allocation');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sourceAccountId || !tellerId) {
      toast.error('Please select a source account and teller');
      return;
    }

    let finalAmount;
    if (useDenominations) {
      finalAmount = calculateDenominationTotal();
      if (finalAmount <= 0) {
        toast.error('Please enter denomination quantities');
        return;
      }
    } else {
      finalAmount = parseFloat(amount);
      if (!finalAmount || finalAmount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
    }

    const maxAmount = selectedSource?.balance || 0;
    if (finalAmount > maxAmount) {
      toast.error(`Insufficient balance. Available: ${formatCurrency(maxAmount, currency)}`);
      return;
    }

    mutation.mutate({
      source_account_id: sourceAccountId,
      teller_account_id: tellerId,
      currency,
      amount: finalAmount,
      denominations: useDenominations ? denominations : undefined,
      notes,
    });
  };

  if (!isOpen) return null;

  const maxAmount = selectedSource?.balance || 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Create Vault Allocation</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Source Account *</label>
            <select
              value={sourceAccountId}
              onChange={(e) => handleSourceChange(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="select-source-account"
            >
              <option value="">Select source account</option>
              {sourceAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} - {a.name} ({a.currency}) - {formatCurrency(a.balance, a.currency)}
                </option>
              ))}
            </select>
            {sourceAccounts.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                No eligible accounts found. Create a Bank or Cash and Equivalents asset account in Chart of Accounts first.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Destination Teller *</label>
            <select
              value={tellerId}
              onChange={(e) => setTellerId(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="select-teller"
            >
              <option value="">Select teller</option>
              {tellers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} - {t.name} (USD: {formatCurrency(t.usd_current_balance)} | LRD: {formatCurrency(t.lrd_current_balance, 'LRD')})
                </option>
              ))}
            </select>
          </div>

          {/* Currency - Auto-set from source account */}
          <div>
            <label className="block text-sm font-medium mb-1">Currency</label>
            <div className={`px-3 py-2 rounded-md border ${
              currency === 'USD' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              {currency === 'USD' ? 'USD ($) - US Dollar' : 'LRD (L$) - Liberian Dollar'}
              {selectedSource && (
                <span className="text-xs ml-2 opacity-75">
                  (Inherited from source account)
                </span>
              )}
            </div>
          </div>

          {/* Amount Input Method Toggle */}
          <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={useDenominations}
                onChange={() => setUseDenominations(true)}
                className="text-primary"
              />
              <span className="text-sm font-medium">By Denomination</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!useDenominations}
                onChange={() => setUseDenominations(false)}
                className="text-primary"
              />
              <span className="text-sm font-medium">Lump Sum</span>
            </label>
          </div>

          {/* Denomination Input or Amount */}
          {useDenominations ? (
            <div className="p-3 bg-muted/30 rounded-lg border border-border">
              <DenominationInput
                currency={currency}
                denominations={denominations}
                onChange={setDenominations}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">
                Amount *
                {selectedSource && (
                  <span className="text-muted-foreground font-normal ml-2">
                    (Max: {formatCurrency(maxAmount, currency)})
                  </span>
                )}
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                max={maxAmount}
                placeholder={`Enter ${currency} amount`}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-amount"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes (e.g., purpose of allocation)"
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-notes"
            />
          </div>

          {/* Summary */}
          {(useDenominations ? calculateDenominationTotal() > 0 : parseFloat(amount) > 0) && selectedSource && tellerId && (
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-sm font-medium mb-2">Allocation Summary</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">From:</span>
                  <span className="font-medium">{selectedSource.code} - {selectedSource.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">To:</span>
                  <span className="font-medium">{tellers.find(t => t.id === tellerId)?.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-semibold text-primary">
                    {formatCurrency(useDenominations ? calculateDenominationTotal() : parseFloat(amount) || 0, currency)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              data-testid="button-create-allocation"
            >
              {mutation.isPending ? 'Creating...' : 'Create Allocation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function VaultAllocations() {
  const { selectedCompany, getDFSCompany } = useCompany();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const dfsCompany = getDFSCompany ? getDFSCompany() : selectedCompany;
  const companyId = dfsCompany?.id;

  const { data: allocations = [], isLoading } = useQuery({
    queryKey: ['allocations', companyId, statusFilter],
    queryFn: () => allocationAPI.getAll(companyId, statusFilter).then((res) => res.data),
    enabled: !!companyId,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id) => allocationAPI.acknowledge(id),
    onSuccess: () => {
      toast.success('Allocation acknowledged');
      queryClient.invalidateQueries(['allocations']);
      queryClient.invalidateQueries(['teller-accounts']);
      queryClient.invalidateQueries(['accounts']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge allocation');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id) => allocationAPI.cancel(id),
    onSuccess: () => {
      toast.success('Allocation cancelled');
      queryClient.invalidateQueries(['allocations']);
      queryClient.invalidateQueries(['accounts']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to cancel allocation');
    },
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries(['allocations', companyId]);
    queryClient.invalidateQueries(['accounts', companyId]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const pendingCount = allocations.filter((a) => a.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="vault-allocations-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Vault Allocations</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Distribute cash from a Chart of Accounts source account to teller workstations with denomination tracking
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          data-testid="button-create-allocation"
        >
          <Plus className="h-4 w-4" />
          New Allocation
        </button>
      </div>

      {/* Filter and Summary */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 bg-background border border-border rounded-md text-sm"
            data-testid="select-status-filter"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 text-sm text-yellow-600 bg-yellow-500/10 px-3 py-1.5 rounded-full">
            <Clock className="h-4 w-4" />
            {pendingCount} pending allocation{pendingCount > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Allocations Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-3 px-4 font-medium">Date</th>
                <th className="text-left py-3 px-4 font-medium">Source Account</th>
                <th className="text-left py-3 px-4 font-medium">Teller</th>
                <th className="text-left py-3 px-4 font-medium">Currency</th>
                <th className="text-right py-3 px-4 font-medium">Amount</th>
                <th className="text-center py-3 px-4 font-medium">Denominations</th>
                <th className="text-center py-3 px-4 font-medium">Status</th>
                <th className="text-center py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((alloc) => {
                // Check if allocation has denominations
                const hasDenoms = alloc.denominations &&
                  Object.values(alloc.denominations).some(v => v > 0);

                return (
                  <tr key={alloc.id} className="border-t border-border/50 hover:bg-muted/30">
                    <td className="py-3 px-4">
                      {new Date(alloc.allocated_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      {alloc.source_account_code ? `${alloc.source_account_code} - ` : ''}{alloc.source_account_name}
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-medium">{alloc.teller_code}</span>
                      <span className="text-muted-foreground ml-1">({alloc.teller_name})</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        alloc.currency === 'USD' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {alloc.currency}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-medium">
                      {formatCurrency(alloc.amount, alloc.currency)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {hasDenoms ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                          <Banknote className="h-3 w-3" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">Lump sum</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        alloc.status === 'pending' ? 'bg-yellow-500/10 text-yellow-600' :
                        alloc.status === 'acknowledged' ? 'bg-green-500/10 text-green-600' :
                        'bg-red-500/10 text-red-600'
                      }`}>
                        {alloc.status === 'pending' && <Clock className="h-3 w-3" />}
                        {alloc.status === 'acknowledged' && <CheckCircle2 className="h-3 w-3" />}
                        {alloc.status === 'cancelled' && <XCircle className="h-3 w-3" />}
                        {alloc.status.charAt(0).toUpperCase() + alloc.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {alloc.status === 'pending' && (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => acknowledgeMutation.mutate(alloc.id)}
                            disabled={acknowledgeMutation.isPending}
                            className="p-1.5 text-green-600 hover:bg-green-500/10 rounded-md"
                            title="Acknowledge"
                            data-testid={`button-acknowledge-${alloc.id}`}
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => cancelMutation.mutate(alloc.id)}
                            disabled={cancelMutation.isPending}
                            className="p-1.5 text-red-600 hover:bg-red-500/10 rounded-md"
                            title="Cancel"
                            data-testid={`button-cancel-${alloc.id}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {alloc.status !== 'pending' && (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {allocations.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-muted-foreground">
                    No allocations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      <CreateAllocationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleRefresh}
        companyId={companyId}
      />
    </div>
  );
}
